// Tablet mode takes the staff sign-in OFF the device while a patient holds it.
//
// The care app keeps the staff session in localStorage.auth (the access token,
// refreshed in place by api.js). Left there, a patient could leave the overlay
// (reload, swipe out of fullscreen and open a new tab, history) and land in
// the signed-in staff app. So on Start:
//
//   - localStorage.auth is moved into memory (this module) and removed from
//     storage: a reload, a new tab or another window sees no staff session and
//     lands on the sign-in page;
//   - THIS page keeps working (the staff app behind the overlay still polls,
//     and api.js may refresh the token): Storage.prototype getItem / setItem /
//     removeItem are shimmed for the "auth" key of window.localStorage only, so
//     every module that reads localStorage.getItem("auth") gets the in-memory
//     token, and a refreshed token goes to memory, never to storage;
//   - a marker in sessionStorage (this tab only) records that tablet mode was
//     on, so a reload boots into a "return the device to staff" screen
//     (kioskRelockPending) instead of the staff app or the sign-in form;
//   - the PIN lives here too (memory only), so the lock survives the overlay
//     component remounting; wrong PINs back off (3 free tries, then 30 s,
//     60 s, 120 s, then 5 min per try).
//
// A correct PIN (unlockStaffSession) puts the token back in localStorage and
// removes the shim and the marker. A reload during tablet mode therefore signs
// staff out on that device: that is the point.
//
// History (Back, the iPad edge swipe) is guarded twice:
//   - while the patient holds the device the overlay keeps one extra history
//     entry for the same URL (pushHistoryGuard), so Back stays on this page;
//   - every page of the app installs installKioskPageGuard at load (App.js):
//     an earlier staff page of this tab restored from the browser's
//     back/forward cache while the tablet-mode marker is set is hidden and
//     reloaded at once, and the reload boots into "return the device". Without
//     it that page would come back exactly as it was (e.g. the dashboard it
//     showed), with no overlay, although its token is gone.
//
// Not covered here, see docs/forms/TABLET_MODE.md: other tabs that were
// already open on the device keep what they already show (their next API call
// fails and sends them to sign-in), so the SOP is iPad Guided Access / Android
// screen pinning with a single care tab.

const AUTH_KEY = "auth";
export const KIOSK_LOCK_KEY = "bcf.kiosk.lock";
export const FREE_PIN_TRIES = 3;

let state = null; // { ls, proto, orig, token, pin, fails, lockedUntil }

const now = () => Date.now();

function localStore() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

function sessionStore() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

export const isStaffSessionLocked = () => !!state;

// Seconds to wait after `fails` wrong PINs (0 = may try now).
export function backoffSeconds(fails) {
  if (fails <= FREE_PIN_TRIES) return 0;
  const n = fails - FREE_PIN_TRIES - 1; // 0, 1, 2, ...
  return n >= 3 ? 300 : 30 * 2 ** n;
}

// Start tablet mode: move the staff token out of storage, remember the PIN.
export function lockStaffSession(pin) {
  if (state) {
    state.pin = String(pin);
    return true;
  }
  const ls = localStore();
  const proto = ls ? Object.getPrototypeOf(ls) : null;
  const ss = sessionStore();
  if (ss) {
    try {
      ss.setItem(KIOSK_LOCK_KEY, String(now()));
    } catch (_) {
      /* private mode: the shim still keeps the token out of storage */
    }
  }
  if (!ls || !proto || typeof proto.getItem !== "function") {
    state = { ls: null, proto: null, orig: null, token: null, pin: String(pin), fails: 0, lockedUntil: 0 };
    return false;
  }
  const orig = { getItem: proto.getItem, setItem: proto.setItem, removeItem: proto.removeItem };
  let token = null;
  try {
    token = orig.getItem.call(ls, AUTH_KEY);
    orig.removeItem.call(ls, AUTH_KEY);
  } catch (_) {
    /* blocked storage: nothing to move */
  }
  const s = { ls, proto, orig, token, pin: String(pin), fails: 0, lockedUntil: 0 };
  proto.getItem = function getItem(key) {
    if (this === s.ls && String(key) === AUTH_KEY) return s.token;
    return orig.getItem.call(this, key);
  };
  proto.setItem = function setItem(key, value) {
    if (this === s.ls && String(key) === AUTH_KEY) {
      s.token = String(value);
      return undefined;
    }
    return orig.setItem.call(this, key, value);
  };
  proto.removeItem = function removeItem(key) {
    if (this === s.ls && String(key) === AUTH_KEY) {
      s.token = null;
      return undefined;
    }
    return orig.removeItem.call(this, key);
  };
  state = s;
  return true;
}

// End tablet mode: token back to storage, shim and marker removed.
export function unlockStaffSession() {
  const s = state;
  state = null;
  if (s && s.proto && s.orig) {
    s.proto.getItem = s.orig.getItem;
    s.proto.setItem = s.orig.setItem;
    s.proto.removeItem = s.orig.removeItem;
    if (s.token) {
      try {
        s.orig.setItem.call(s.ls, AUTH_KEY, s.token);
      } catch (_) {
        /* storage full / blocked: staff sign in again */
      }
    }
  }
  clearKioskRelock();
}

// Check a PIN entry. {ok:true} | {ok:false, waitSeconds, triesLeft}.
// While waiting, entries are refused without counting.
export function checkPin(entry) {
  const s = state;
  if (!s) return { ok: true };
  const t = now();
  if (s.lockedUntil > t) {
    return { ok: false, waitSeconds: Math.ceil((s.lockedUntil - t) / 1000), triesLeft: 0 };
  }
  if (String(entry) === s.pin) {
    s.fails = 0;
    s.lockedUntil = 0;
    return { ok: true };
  }
  s.fails += 1;
  const wait = backoffSeconds(s.fails);
  s.lockedUntil = wait ? t + wait * 1000 : 0;
  return { ok: false, waitSeconds: wait, triesLeft: Math.max(0, FREE_PIN_TRIES - s.fails) };
}

// Seconds until another PIN may be tried (0 = now).
export function pinWaitSeconds() {
  if (!state || state.lockedUntil <= now()) return 0;
  return Math.ceil((state.lockedUntil - now()) / 1000);
}

// This tab was in tablet mode and the page was reloaded (or the tab was
// duplicated): the in-memory sign-in is gone; show "return the device".
export function kioskRelockPending() {
  if (state) return false;
  const ss = sessionStore();
  if (!ss) return false;
  try {
    return !!ss.getItem(KIOSK_LOCK_KEY);
  } catch (_) {
    return false;
  }
}

export function clearKioskRelock() {
  const ss = sessionStore();
  if (!ss) return;
  try {
    ss.removeItem(KIOSK_LOCK_KEY);
  } catch (_) {
    /* nothing to clear */
  }
}

// ── history: Back during tablet mode ─────────────────────────────────────────
const GUARD_KEY = "bcfKioskGuard";

// One extra history entry for the current URL (same router state plus a flag),
// so Back lands on this page again instead of an earlier one. Call it from a
// user gesture (Start): browsers may skip entries pushed without one.
export function pushHistoryGuard(win) {
  const w = win || (typeof window !== "undefined" ? window : null);
  if (!w || !w.history || typeof w.history.pushState !== "function") return false;
  try {
    const st = w.history.state;
    if (st && typeof st === "object" && st[GUARD_KEY]) return false;
    w.history.pushState({ ...(st && typeof st === "object" ? st : {}), [GUARD_KEY]: true }, "", w.location.href);
    return true;
  } catch (_) {
    return false;
  }
}

// A page restored from the back/forward cache while this tab is in tablet
// mode (marker set, and this page never held the lock): hide it, reload it.
// `reload` is for tests (jsdom cannot navigate).
let pageGuard = null;
export function installKioskPageGuard(win, reload) {
  const w = win || (typeof window !== "undefined" ? window : null);
  if (!w || typeof w.addEventListener !== "function") return false;
  if (pageGuard && pageGuard.w === w) return false;
  const onShow = (e) => {
    if (!e || !e.persisted || !kioskRelockPending()) return;
    try {
      if (w.document && w.document.documentElement) w.document.documentElement.style.visibility = "hidden";
    } catch (_) {
      /* still reload */
    }
    try {
      if (reload) reload();
      else w.location.reload();
    } catch (_) {
      /* nothing more a page can do */
    }
  };
  w.addEventListener("pageshow", onShow);
  pageGuard = { w, onShow };
  return true;
}

export function uninstallKioskPageGuard() {
  if (!pageGuard) return;
  try {
    pageGuard.w.removeEventListener("pageshow", pageGuard.onShow);
  } catch (_) {
    /* gone */
  }
  pageGuard = null;
}

// ── fullscreen (standard + WebKit; iPhone Safari has neither for elements) ───
export function canFullscreen(doc) {
  const d = doc || (typeof document !== "undefined" ? document : null);
  if (!d) return false;
  const el = d.documentElement || {};
  const api = el.requestFullscreen || el.webkitRequestFullscreen || el.webkitRequestFullScreen;
  const enabled = d.fullscreenEnabled !== undefined ? d.fullscreenEnabled
    : d.webkitFullscreenEnabled !== undefined ? d.webkitFullscreenEnabled
      : d.webkitFullScreenEnabled;
  return typeof api === "function" && enabled !== false;
}

// Ask for element fullscreen; true when a request was made.
export function enterFullscreen(el) {
  if (!el) return false;
  const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.webkitRequestFullScreen;
  if (typeof fn !== "function") return false;
  try {
    const p = fn.call(el);
    if (p && typeof p.catch === "function") p.catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
}

export function leaveFullscreen(doc) {
  const d = doc || (typeof document !== "undefined" ? document : null);
  if (!d) return;
  try {
    if (d.fullscreenElement && typeof d.exitFullscreen === "function") {
      const p = d.exitFullscreen();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else if (d.webkitFullscreenElement && typeof d.webkitExitFullscreen === "function") {
      d.webkitExitFullscreen();
    }
  } catch (_) {
    /* not in fullscreen */
  }
}
